import { useContext } from 'react';
import { ActivityIndicator } from 'react-native';
import { useRoute } from '@react-navigation/native';
import { Flex, StackScreen, ADSContext, Typography } from '@banhala/ui-kit';
import { AtomicItemListType, AtomicHeaderType } from 'shared/src/api';
import {
  ProfileInfo,
  Divider,
  GoodsCarousel,
  TwoColGoodsList,
  ThreeColGoodsList,
  TwoLineMessage,
  Empty,
  NotificationGuide,
  CompanyInfo,
  SearchBar,
  NeonSignBoard,
  Summary,
  HorizontalChipList,
  FourColIconList,
  FiveColIconList,
  ScrollableIconList,
  MarketBadgeList,
  StoreList,
  BannerCarousel,
  DescriptionCardCarousel,
  VerticalBannerList,
} from 'shared/src/component/AtomicRenderer/ItemList';
import { TodayShortBanner } from 'shared/src/component/AtomicRenderer/ItemList/TodayShortBanner';
import { TodayRollingBanner } from 'shared/src/component/AtomicRenderer/ItemList/TodayRollingBanner';
import { FilterList } from 'shared/src/component/AtomicRenderer/ItemList/FilterList';
import { CheckboxFilterList } from 'shared/src/component/AtomicRenderer/ItemList/CheckboxFilterList';
import { DownloadableCoupons } from 'shared/src/component/AtomicRenderer/ItemList/DownloadableCoupons';
import { DownloadedCoupons } from 'shared/src/component/AtomicRenderer/ItemList/DownloadedCoupons';
import { EventGrid } from 'shared/src/component/AtomicRenderer/ItemList/EventGrid';
import { ExpandableCategoryList } from 'shared/src/component/AtomicRenderer/ItemList/ExpandableCategoryList';
import { FlipDescriptionCard } from 'shared/src/component/AtomicRenderer/ItemList/FlipDescriptionCard';
import { FrequentlyVisitedMarketList } from 'shared/src/component/AtomicRenderer/ItemList/FrequentlyVisitedMarketList';
import { GoodsCarouselCalendar } from 'shared/src/component/AtomicRenderer/ItemList/GoodsCarouselCalendar';
import { HorizontalImageChipList } from 'shared/src/component/AtomicRenderer/ItemList/HorizontalImageChipList';
import { InputNonEntryMarket } from 'shared/src/component/AtomicRenderer/ItemList/InputNonEntryMarket';
import { OneCheckboxFilter } from 'shared/src/component/AtomicRenderer/ItemList/OneCheckboxFilter';
import { OutfitCarousel } from 'shared/src/component/AtomicRenderer/ItemList/OutfitCarousel';
import { PromotionShortBanner } from 'shared/src/component/AtomicRenderer/ItemList/PromotionShortBanner';
import { SortingGoodsList } from 'shared/src/component/AtomicRenderer/ItemList/SortingGoodsList';
import { TasteOnboardingCard } from 'shared/src/component/AtomicRenderer/ItemList/TasteOnboardingCard';
import { ThreeColFilter } from 'shared/src/component/AtomicRenderer/ItemList/ThreeColFilter';
import { TwoColBrandGoodsList } from 'shared/src/component/AtomicRenderer/ItemList/TwoColBrandGoodsList';
import { TwoColFeaturedShortcutList } from 'shared/src/component/AtomicRenderer/ItemList/TwoColFeaturedShortcutList';
import {
  HeadlineSmallOneLine,
  HeadlineXsmallOneLine,
  HeadlineSmallOneLineWithText,
  HeadlineSmallOneLineWithTextCompact,
  HeadlineOnlyText,
  HeadlineSmallTwoLineWithTimer,
  HeadlineLargeTwoLine,
  HeadlineSmallOneLineCompact,
} from 'shared/src/component/AtomicRenderer/Header';
import { HeadlineOverlineTimer } from 'shared/src/component/AtomicRenderer/Header/HeadlineOverlineTimer';
import { HeadlineSelectableOneLine } from 'shared/src/component/AtomicRenderer/Header/HeadlineSelectableOneLine';
import { HeadlineSmallOneLineToggle } from 'shared/src/component/AtomicRenderer/Header/HeadlineSmallOneLineToggle';
import { HeadlineSmallOneLineWithTimer } from 'shared/src/component/AtomicRenderer/Header/HeadlineSmallOneLineWithTimer';
import { HeadlineSmallTwoLine } from 'shared/src/component/AtomicRenderer/Header/HeadlineSmallTwoLine';
import { HeadlineXsmallOneLineCompact } from 'shared/src/component/AtomicRenderer/Header/HeadlineXsmallOneLineCompact';
import { HeadlineXsmallOneLineSegments } from 'shared/src/component/AtomicRenderer/Header/HeadlineXsmallOneLineSegments';
import { HeadlineXsmallOneLineToggle } from 'shared/src/component/AtomicRenderer/Header/HeadlineXsmallOneLineToggle';
import { useGetComponentData } from './useGetComponentData';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ItemListComponent: Record<string, React.ComponentType<any>> = {
  [AtomicItemListType.PROFILE_INFO]: ProfileInfo,
  [AtomicItemListType.Divider]: Divider,
  [AtomicItemListType.GoodsCarousel]: GoodsCarousel,
  [AtomicItemListType.FilterList]: FilterList,
  [AtomicItemListType.TwoColGoodsList]: TwoColGoodsList,
  [AtomicItemListType.TwoLineMessage]: TwoLineMessage,
  [AtomicItemListType.Empty]: Empty,
  [AtomicItemListType.NotificationGuide]: NotificationGuide,
  [AtomicItemListType.CompanyInfo]: CompanyInfo,
  [AtomicItemListType.SearchBar]: SearchBar,
  [AtomicItemListType.NeonSignBoard]: NeonSignBoard,
  [AtomicItemListType.Summary]: Summary,
  [AtomicItemListType.HorizontalChipList]: HorizontalChipList,
  [AtomicItemListType.FourColIconList]: FourColIconList,
  [AtomicItemListType.FiveColIconList]: FiveColIconList,
  [AtomicItemListType.ScrollableIconList]: ScrollableIconList,
  [AtomicItemListType.MarketBadgeList]: MarketBadgeList,
  [AtomicItemListType.ThreeColGoodsList]: ThreeColGoodsList,
  [AtomicItemListType.StoreList]: StoreList,
  [AtomicItemListType.BannerCarousel]: BannerCarousel,
  [AtomicItemListType.DescriptionCardCarousel]: DescriptionCardCarousel,
  [AtomicItemListType.VerticalBannerList]: VerticalBannerList,
  [AtomicItemListType.TodayShortBanner]: TodayShortBanner,
  [AtomicItemListType.TodayRollingBanner]: TodayRollingBanner,
  [AtomicItemListType.CheckboxFilterList]: CheckboxFilterList,
  [AtomicItemListType.DownloadableCoupons]: DownloadableCoupons,
  [AtomicItemListType.DownloadedCoupons]: DownloadedCoupons,
  [AtomicItemListType.EventGrid]: EventGrid,
  [AtomicItemListType.ExpandableCategoryList]: ExpandableCategoryList,
  [AtomicItemListType.FlipDescriptionCard]: FlipDescriptionCard,
  [AtomicItemListType.FrequentlyVisitedMarketList]: FrequentlyVisitedMarketList,
  [AtomicItemListType.GoodsCarouselCalendar]: GoodsCarouselCalendar,
  [AtomicItemListType.HorizontalImageChipList]: HorizontalImageChipList,
  [AtomicItemListType.InputNonEntryMarket]: InputNonEntryMarket,
  [AtomicItemListType.OneCheckboxFilter]: OneCheckboxFilter,
  [AtomicItemListType.OutfitCarousel]: OutfitCarousel,
  [AtomicItemListType.PromotionShortBanner]: PromotionShortBanner,
  [AtomicItemListType.SortingGoodsList]: SortingGoodsList,
  [AtomicItemListType.TasteOnboardingCard]: TasteOnboardingCard,
  [AtomicItemListType.ThreeColFilter]: ThreeColFilter,
  [AtomicItemListType.TwoColBrandGoodsList]: TwoColBrandGoodsList,
  [AtomicItemListType.TwoColFeaturedShortcutList]: TwoColFeaturedShortcutList,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const HeaderComponent: Record<string, React.ComponentType<any>> = {
  [AtomicHeaderType.HeadlineSmallOneLine]: HeadlineSmallOneLine,
  [AtomicHeaderType.HeadlineXsmallOneLine]: HeadlineXsmallOneLine,
  [AtomicHeaderType.HeadlineSmallOneLineWithText]: HeadlineSmallOneLineWithText,
  [AtomicHeaderType.HeadlineSmallOneLineWithTextCompact]:
    HeadlineSmallOneLineWithTextCompact,
  [AtomicHeaderType.HeadlineOnlyText]: HeadlineOnlyText,
  [AtomicHeaderType.HeadlineSmallTwoLineWithTimer]:
    HeadlineSmallTwoLineWithTimer,
  [AtomicHeaderType.HeadlineLargeTwoLine]: HeadlineLargeTwoLine,
  [AtomicHeaderType.HeadlineSmallOneLineCompact]: HeadlineSmallOneLineCompact,
  [AtomicHeaderType.HeadlineOverlineTimer]: HeadlineOverlineTimer,
  [AtomicHeaderType.HeadlineSelectableOneLine]: HeadlineSelectableOneLine,
  [AtomicHeaderType.HeadlineSmallOneLineToggle]: HeadlineSmallOneLineToggle,
  [AtomicHeaderType.HeadlineSmallOneLineWithTimer]: HeadlineSmallOneLineWithTimer,
  [AtomicHeaderType.HeadlineSmallTwoLine]: HeadlineSmallTwoLine,
  [AtomicHeaderType.HeadlineXsmallOneLineCompact]: HeadlineXsmallOneLineCompact,
  [AtomicHeaderType.HeadlineXsmallOneLineSegments]: HeadlineXsmallOneLineSegments,
  [AtomicHeaderType.HeadlineXsmallOneLineToggle]: HeadlineXsmallOneLineToggle,
};

interface ScreenRendererCoreProps {
  type: string;
}

// Core 컴포넌트 - Navigation 없이 독립적으로 사용 가능
export const ScreenRendererCore = ({ type }: ScreenRendererCoreProps) => {
  const { contentWidth } = useContext(ADSContext);
  const { data, isLoading, error } = useGetComponentData(type);

  if (isLoading) {
    return (
      <Flex.Center flex={1} backgroundColor="white">
        <ActivityIndicator size="large" />
      </Flex.Center>
    );
  }

  if (error) {
    return (
      <Flex.Center flex={1} backgroundColor="white" padding={{ horizontal: 16, vertical: 16 }}>
        <Typography type="body2" rawColor="red" textAlign="center">
          Error: {error instanceof Error ? error.message : 'Unknown error'}
        </Typography>
      </Flex.Center>
    );
  }

  if (!data) {
    return (
      <Flex.Center flex={1} backgroundColor="white" padding={{ horizontal: 16, vertical: 16 }}>
        <Typography type="body2" rawColor="red" textAlign="center">
          No data available
        </Typography>
      </Flex.Center>
    );
  }

  const headerType = data.type.header;
  const itemListType = data.type.item_list;

  if (!itemListType) {
    return (
      <Flex.Center flex={1} backgroundColor="white" padding={{ horizontal: 16, vertical: 16 }}>
        <Typography type="body2" rawColor="red" textAlign="center">
          No item_list type in component
        </Typography>
      </Flex.Center>
    );
  }

  const Header = headerType ? HeaderComponent[headerType] : null;
  const Component = ItemListComponent[itemListType];

  if (!Component) {
    return (
      <Flex.Center flex={1} backgroundColor="white" padding={{ horizontal: 16, vertical: 16 }}>
        <Typography type="body2" rawColor="red" textAlign="center">
          Unsupported component type: {itemListType}
        </Typography>
      </Flex.Center>
    );
  }

  return (
    <Flex width={contentWidth} alignSelf="center">
      {Header && (
        <Header
          header={
            data.entity.header
              ? {
                  ...data.entity.header,
                  logging: data.entity.header.logging ?? data.logging,
                }
              : null
          }
        />
      )}
      <Component
        entity={data.entity.item_list}
        body={data.entity.body}
        logging={data.logging}
        scrollToFilterList={() => {}}
        likeMap={{}}
        setLikeMap={() => {}}
      />
    </Flex>
  );
};

interface ScreenRendererRouteParams {
  instanceId: string;
  type: string;
}

// Navigation Stack용 wrapper
export const ScreenRenderer = () => {
  const route = useRoute<{
    key: string;
    name: string;
    params: ScreenRendererRouteParams;
  }>();
  const { type } = route.params;

  return (
    <StackScreen title={`ScreenRenderer: ${type}`}>
      <ScreenRendererCore type={type} />
    </StackScreen>
  );
};

